import {Injectable} from '@angular/core';
import {initializeApp, getApps} from 'firebase/app';
import {getMessaging, getToken, isSupported, onMessage} from 'firebase/messaging';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {Principal} from 'app/core/auth/principal.service';
import {UserDeviceService} from 'app/blocks/service/api/user-device.service';
import versionJson from '../../../../version.json';

type FirebaseConfig = {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
};

type FirebaseWebConfig = {
    firebaseConfig: FirebaseConfig;
    firebaseVapidKey: string;
};

@Injectable({providedIn: 'root'})
export class PushNotificationService {
    private initialized = false;
    private readonly logPrefix = '[PushNotificationService]';

    constructor(
        private notificationService: NzNotificationService,
        private principal: Principal,
        private userDeviceService: UserDeviceService
    ) {}

    async initialize(): Promise<void> {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        const webConfig = await this.loadWebConfig();
        if (!webConfig) {
            console.warn('Push notifications disabled: missing Firebase config or VAPID key.');
            return;
        }
        const firebaseConfig = webConfig.firebaseConfig;
        const vapidKey = webConfig.firebaseVapidKey;

        const supported = await isSupported();
        if (!supported) {
            console.warn('Push notifications are not supported in this browser.');
            return;
        }

        if (Notification.permission === 'denied') {
            console.warn('Push notifications are blocked by the user.');
            return;
        }

        if (getApps().length === 0) {
            initializeApp(firebaseConfig);
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.warn('Push notification permission not granted.');
            return;
        }

        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        const messaging = getMessaging();
        const token = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: registration
        });

        if (!token) {
            console.warn('Failed to get FCM token.');
            return;
        }

        console.log(`${this.logPrefix} FCM token received: ${this.maskToken(token)}`);
        await this.registerUserDevice(token);
        onMessage(messaging, (payload) => {
            const title = payload?.notification?.title || 'RouteMagic';
            const body = payload?.notification?.body || '';
            this.notificationService.info(title, body);
        });
    }

    private async loadWebConfig(): Promise<FirebaseWebConfig | null> {
        try {
            const response = await fetch('/assets/firebase-config.json', {cache: 'no-store'});
            if (!response.ok) {
                return null;
            }
            const config = (await response.json()) as FirebaseWebConfig;
            if (!config?.firebaseConfig || !config?.firebaseVapidKey || !this.isFirebaseConfigValid(config.firebaseConfig)) {
                return null;
            }
            return config;
        } catch (error) {
            console.warn('Failed to load firebase-config.json', error);
            return null;
        }
    }

    private isFirebaseConfigValid(firebaseConfig?: FirebaseConfig): boolean {
        if (!firebaseConfig) {
            return false;
        }
        return Boolean(
            firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId
        );
    }

    private async registerUserDevice(token: string): Promise<void> {
        try {
            const identity = await this.principal.identity();
            if (!identity) {
                console.info(`${this.logPrefix} Skipping device registration: user not authenticated.`);
                return;
            }

            await this.userDeviceService.registerUserDevice({
                platformType: 'WEB',
                platformOs: 'WEB',
                notificationProviderType: 'WEB_FCM',
                notificationToken: token,
                appVersion: versionJson.appVersion
            });
            console.log(`${this.logPrefix} Device registered for push notifications.`);
        } catch (error) {
            console.error(`${this.logPrefix} Failed to register device for push notifications.`, error);
        }
    }

    private maskToken(token: string): string {
        if (!token || token.length <= 10) {
            return '***';
        }
        return `${token.slice(0, 6)}...${token.slice(-4)}`;
    }
}
